import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import CustomColumnManager from '@/Components/CustomColumnManager/CustomColumnManager';
import Navigation from '@/Components/Navigation/Navigation';
import { getMenuItems } from '@/Components/Spreadsheet/ToolPanel/config';
import ViewOnly from '@/Components/Spreadsheet/ToolPanel/ViewOnly';
import ShowToast from '@/Components/Toast/showToastTemplate';
import { Button } from '@/Components/UI/Button/Button';
import Icon from '@/Components/UI/Icon/Icon';
import Popover from '@/Components/UI/Popover/Popover';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { exportAllRows } from '@/helpers/exportHelper';
import {
  getGridRequestData as _getGridRequestData,
  addRow,
  handleDeleteRow,
  hasNoWritePermission,
} from '@/helpers/spreadsheetHelper';
import { useCanExportOrSave } from '@/hooks/useCanExportOrSave';
import useViewportProvider from '@/hooks/useViewportProvider';
import {
  datasetSliceName,
  modalsSliceName,
  spreadsheetSliceName,
  userDataSliceName,
} from '@/redux/constants';
import { fetchOpenedFile } from '@/redux/reducers/datasetSlice';
import { showModal } from '@/redux/reducers/modalsSlice';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import {
  applyFormula,
  getAllViews,
  initialState,
  restoreGridState,
  setAggregationEndpointValues,
  setColumnDefs,
  setColumnState,
  setGridSidePanel,
  setIsAggregationEndpointValuesLoading,
  setIsInsertingNewColumn,
  setIsMobileExperienceModalOpen,
  setIsOpenFileNameMenu,
  setProperMobileDrawerContent,
} from '@/redux/reducers/spreadsheetSlice';
import { get, post } from '@/Utils/API';
import {
  AGGREGATION,
  COLUMN_MANAGER,
  FILE,
  FORMULA,
  MODAL_AUDIT_LOG,
  MODAL_CREATE_ACCOUNT,
  MODAL_DATA_ENRICHMENT,
  MODAL_DELETE_COLUMNS,
  MODAL_DELETE_DUPLICATES,
  MODAL_DELETE_ROW,
  MODAL_EXPORT_SPREADSHEET,
  MODAL_FILTER_PANEL,
  MODAL_GENERAL_UPGRADE,
  MODAL_GROUP_BY,
  MODAL_LIVE_QUERY,
  MODAL_MAKE_COPY,
  MODAL_MAKE_COPY_FILE,
  MODAL_PASTE_FROM_CONTEXT_MENU,
  MODAL_REQUIRE_ACCOUNT,
  MODAL_SHARE_FILE,
  MODAL_SORT_COLUMN,
  MODAL_SPLIT_SHEET,
  PERMISSION_WRITE,
  SIDE_PANEL_INFO_PANEL,
  TOAST_TEXT_APPLY_FUNCTION_ERROR,
  TOAST_TYPE_ERROR,
  URL_DATASET,
  URL_SPREADSHEET,
} from '@/Utils/constants';
import { getCountFiltersInModel } from '@/Utils/getCountFiltersInModel';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { getSelectionRanges } from '@/Utils/getSelectionRanges';
import getTopRangeRowsIndexAndRowCount from '@/Utils/getTopRangeRowsIndexAndRowCount';
import { isRevenueBaseFile, openInNewTab, openSpreadsheet } from '@/Utils/utils';
import { ControlledMenu, MenuItem, SubMenu } from '@szhsin/react-menu';

import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';

import Views from '@/Components/Views/Views';
import { useCurrentFile } from '@/hooks/useCurrentFile';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';
import { getClientState } from '@/Utils/ClientStateCommon';
import { parseRowCount } from '@/Utils/DatasetTree/parseRowCount';
import { AG_GRID_AUTO_COLUMN, ROW_NUMBER_COLUMN_ID } from '@/Utils/gridConstants';
import { useNavigate, useParams } from 'react-router-dom';
import './styles.scss';

const ONE_MILLION_ROWS = 1000000;

const CREATE_ACCT = MODAL_CREATE_ACCOUNT;
const FILTER = MODAL_FILTER_PANEL;
const GROUP = MODAL_GROUP_BY;
const SORT = MODAL_SORT_COLUMN;

const CustomMenu = ({ menuConfig, isMenuItemsDisabled, openModal, setIsMenuOpen }) => {
  const { user } = useSelector((state) => state[userDataSliceName]);
  const currentFile = useCurrentFile();
  const { modalsQueue } = useSelector((state) => state[modalsSliceName]);
  const [toolPanelItemStatus, setToolPanelItemStatus] = useState('closed');
  const ref = useRef(null);

  useEffect(() => {
    setIsMenuOpen(toolPanelItemStatus);
  }, [toolPanelItemStatus]);

  const isCreateFolderModalOpen = modalsQueue.some(({ name }) => name === 'createFolderModal');

  const renderMenuItems = (menu = []) => {
    return menu.map((menuItem) => {
      const allowedDataSubMenus = [
        'Sort',
        'Filter',
        'Group',
        'Manage Columns',
        'Databricks',
        'Snowflake',
      ];
      if (isMenuItemsDisabled && !allowedDataSubMenus.includes(menuItem.title)) {
        const originalAction = menuItem.action;

        menuItem.action = () => {
          if (user?.is_anonymous) {
            openModal(CREATE_ACCT);
          } else {
            originalAction();
          }
        };
        menuItem.hidden = false;
      }

      const {
        title,
        action,
        separator = false,
        icon = '',
        iconAsImage = '',
        submenu = [],
        premiumFeature = false,
        hidden = false,
      } = menuItem;

      const onMenuItemClick = (event) => {
        event?.syntheticEvent?.stopPropagation();
        if (action && typeof action === 'function') {
          action();
        }
      };

      if (!submenu.length) {
        return (
          <Fragment key={title}>
            {separator && <hr className='w-full my-1 border-ui' />}
            <MenuItem
              onClick={onMenuItemClick}
              key={title}
              hidden={menuItem.hidden}
              disabled={menuItem.disabled}
              data-cy={`menuItem ${title}`}
              className={clsx(
                'rounded',
                !icon?.length && '!pl-[15px]',
                premiumFeature && '!text-ui-secondary !hover:bg-white !bg-white opacity-80',
                hidden && '!hidden'
              )}
            >
              {iconAsImage ? (
                <img
                  src={iconAsImage}
                  height='16'
                  width='16'
                  alt={`img-${title}`}
                  className='relative right-1 mr-[8px] mb-1'
                />
              ) : (
                <Icon name={icon} size={18} alt={`img-${title}`} className='mx-[10px]' />
              )}
              {title}
              {premiumFeature ? (
                <Icon
                  weight='duotone'
                  name='rocket-launch'
                  size={18}
                  className='absolute right-4'
                  color='#D9B500'
                />
              ) : null}
            </MenuItem>
          </Fragment>
        );
      }

      return (
        <div key={title} onClick={onMenuItemClick} className=''>
          {separator && <hr className='w-full my-1 border-ui' />}
          <SubMenu
            label={
              <>
                <Icon name={icon} size={18} alt={`img-${title}`} className='mx-[10px]' />
                {title}
              </>
            }
          >
            {renderMenuItems(submenu)}
          </SubMenu>
        </div>
      );
    });
  };

  const handleButtonClick = () => {
    if (menuConfig.action && typeof menuConfig.action === 'function') {
      menuConfig.action();
    }
    (!(!user?.is_anonymous && currentFile.isFileShared && hasNoWritePermission(currentFile)) ||
      menuConfig.title === 'File' ||
      menuConfig.title === 'Help') &&
      setToolPanelItemStatus('open');

    menuConfig.title === 'Automation' && setToolPanelItemStatus('closed');
  };

  const renderButtonWithTooltip = (menuConfig, isMenuItemsDisabled, handleButtonClick, ref) => (
    <>
      {menuConfig?.tooltip ? (
        <Tooltip text={menuConfig?.tooltip || ''} side='bottom' asChild>
          {renderButton(menuConfig, isMenuItemsDisabled, handleButtonClick, ref)}
        </Tooltip>
      ) : (
        renderButton(menuConfig, isMenuItemsDisabled, handleButtonClick, ref)
      )}
    </>
  );

  const renderButton = (menuConfig, isMenuItemsDisabled, handleButtonClick, ref) => (
    <Button
      disabled={menuConfig.disabled}
      dataCy={
        isMenuItemsDisabled ? `anon-toolpanel-${menuConfig.title}` : `toolpanel-${menuConfig.title}`
      }
      onClick={handleButtonClick}
      variant='ghost'
      className={clsx('!h-7 mr-1 text-sm toolbar-button', menuConfig.hidden && '!hidden')}
      ref={ref}
    >
      {menuConfig.title}
    </Button>
  );

  return (
    <>
      {renderButtonWithTooltip(menuConfig, isMenuItemsDisabled, handleButtonClick, ref)}
      <ControlledMenu
        state={toolPanelItemStatus}
        anchorRef={ref}
        onClose={() => !isCreateFolderModalOpen && setToolPanelItemStatus('closed')}
      >
        {renderMenuItems(menuConfig.submenu, setToolPanelItemStatus)}
      </ControlledMenu>
    </>
  );
};

export default function ToolPanel({
  chartData,
  getGridRequestData,
  firstRowAsHeader,
  headerAsFirstRow,
  agGrid,
  navigationProps,
  autoSizeAll,
}) {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const { canSplitSheet, enableChatSupport, canShareLiveQuery } = user?.userProperties || {};
  const [columnInfo, setColumnInfo] = useState(null);
  const currentFile = useCurrentFile();
  const params = useParams();

  const {
    clientState: {
      filterModel,
      sortModel,
      alwaysConfirmDeleteRow,
      columnState,
      alwaysConfirm,
      alwaysConfirmChangeCase,
      isPivotEnabled,
      sidePanel,
    },
    filteredNumberOfRows,
    totalGroupCount,
    isInsertingNewColumn,
    selectedRow,
    columnDefs,
    isDeletingRows,
    isDeletingColumns,
    enrichmentCredits,
    sheetViews,
    aggregationEndpointValues,
  } = useSelector((state) => state[spreadsheetSliceName]);
  const { isFileShared, metadata } = currentFile || {};

  const [shouldDisableActions, setShouldDisableActions] = useState(false);
  const [isResetButtonEnable, setIsResetButtonEnable] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState('');
  const { isMobileScreen } = useViewportProvider();
  const [ranges, setRanges] = useState(agGrid?.api?.getCellRanges());

  const { rowCount } = getTopRangeRowsIndexAndRowCount(ranges || []);

  const isMobileExperienceActive = localStorage.getItem('isMobileExperience') === 'true';

  const isCanExportFilteredGroupedRolePaywall = useCanExportOrSave();

  const navigate = useNavigate();

  const canAccessBusinessConnectors = user.userProperties?.canAccessBusinessConnectors;
  const canAccessEnterpriseConnectors = user.userProperties?.canAccessEnterpriseConnectors;

  const isLiveQuery = metadata?.Type === 3;
  const isSnowflakeFile = metadata?.Details?.type?.includes('snowflake');
  const _isRevenueBaseFile = isRevenueBaseFile(currentFile);
  const revenueBaseAccess = !!user?.userProperties?.revenueBaseAccess && _isRevenueBaseFile;
  const limitedAnalystMenu = isViewerAnalyst || revenueBaseAccess;
  const hubspotAccess = _isRevenueBaseFile && revenueBaseAccess;

  const numberOfHiddenColumns = columnState.filter(
    (column) =>
      column.colId !== ROW_NUMBER_COLUMN_ID && column.hasOwnProperty('hide') && column.hide
  ).length;

  useEffect(() => {
    isMenuOpen === 'open' && setRanges(agGrid?.api?.getCellRanges());
  }, [isMenuOpen]);

  useEffect(() => {
    let _isResetButtonDisabled = true;
    if (_isResetButtonDisabled && sortModel?.length) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && !alwaysConfirm) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && !alwaysConfirmChangeCase) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && !alwaysConfirmDeleteRow) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && isPivotEnabled) {
      _isResetButtonDisabled = false;
    }
    if (filterModel && Object.values(filterModel).some((el) => el?.length)) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && columnState?.length && numberOfHiddenColumns) {
      _isResetButtonDisabled = false;
    }
    if (_isResetButtonDisabled && aggregationEndpointValues?.length) {
      _isResetButtonDisabled = false;
    }
    if (
      _isResetButtonDisabled &&
      columnState.length &&
      columnState.some(({ rowGroup }) => rowGroup)
    ) {
      _isResetButtonDisabled = false;
    }

    setIsResetButtonEnable(_isResetButtonDisabled);
  }, [
    filterModel,
    sortModel,
    alwaysConfirmDeleteRow,
    columnState,
    alwaysConfirm,
    alwaysConfirmChangeCase,
    isPivotEnabled,
    numberOfHiddenColumns,
    aggregationEndpointValues,
  ]);

  useEffect(() => {
    setShouldDisableActions(isInsertingNewColumn || isDeletingRows || isDeletingColumns);
  }, [isInsertingNewColumn, isDeletingRows, isDeletingColumns]);

  const isGroupedColumns = columnState.length && columnState.find(({ rowGroup }) => rowGroup);
  const groupedColumnsLength =
    columnState.length && columnState.filter(({ rowGroup }) => rowGroup).length;

  const openMakeCopyFileModal = async (isSaveResultsView = false) => {
    if (user?.is_anonymous) {
      dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
    } else {
      dispatch(
        showModal({
          name: MODAL_MAKE_COPY_FILE,
          props: { getGridRequestData, isCanExportFilteredGroupedRolePaywall, isSaveResultsView },
        })
      );
    }
  };

  const openSharePopup = () => {
    if (user?.is_anonymous) {
      dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
    } else {
      dispatch(showModal({ name: MODAL_SHARE_FILE }));
    }
  };

  const openRenameDropdown = () => {
    if (user?.is_anonymous) {
      dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
    } else {
      dispatch(setIsOpenFileNameMenu());
    }
  };

  const openDeleteRowModal = (rowsToDelete) => {
    user?.is_anonymous
      ? dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }))
      : alwaysConfirmDeleteRow
      ? dispatch(
          showModal({
            name: MODAL_DELETE_ROW,
            props: { rowsToDelete: rowsToDelete, requestBody: { ...getGridRequestData } },
          })
        )
      : handleDeleteRow(rowsToDelete, { ...getGridRequestData });
  };

  const openDeleteColumnsPopup = (columnsToDelete) => {
    user?.is_anonymous
      ? dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }))
      : dispatch(
          showModal({
            name: MODAL_DELETE_COLUMNS,
            props: { columnsToDelete: columnsToDelete },
          })
        );
  };

  const openExportFileModal = () => {
    if (user?.is_anonymous) {
      dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
      return;
    }

    if (isPreviewMode) {
      showPreviewModeUpsellModal();
      return;
    }

    if (
      revenueBaseAccess &&
      (totalGroupCount ? totalGroupCount : filteredNumberOfRows) > ONE_MILLION_ROWS
    ) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: 'This export exceeds the limit of 1,000,000 rows. Please adjust your filters to reduce the number of rows before exporting.',
      });
      return;
    }

    if (!isCanExportFilteredGroupedRolePaywall) {
      exportAllRows(_getGridRequestData(agGrid), metadata, shouldDisableActions, dispatch);
    }

    dispatch(
      showModal({
        name: MODAL_EXPORT_SPREADSHEET,
        props: { getGridRequestData, isCanExportFilteredGroupedRolePaywall },
      })
    );
  };

  const openDeleteDuplicatesModal = () =>
    user?.is_anonymous
      ? dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }))
      : dispatch(showModal({ name: MODAL_DELETE_DUPLICATES }));

  const openSplitToMultipleCVSsPopup = () => {
    if (isPreviewMode) {
      return showPreviewModeUpsellModal('Add Split to Multilple CSVs on the Premium Plan');
    }
    openModal(MODAL_SPLIT_SHEET);

    !canSplitSheet &&
      dispatch(
        addMixpanelEvent({
          eventName: 'Split to Multiple CSVs paywall',
          eventProps: { FunctionName: 'Split to Multiple CSVs' },
          userIncrementName: '# of clicks',
        })
      );
  };

  const disableDelete = {
    singleRow: !selectedRow || shouldDisableActions,
    filteredRows: shouldDisableActions || !filterModel?._cnf_?.length,
    unfilteredRows: shouldDisableActions || !filterModel?._cnf_?.length,
  };

  const lastNumberOfDefaultColumnName = useMemo(
    () =>
      columnDefs.reduce((acc, { headerName }) => {
        if (headerName.startsWith('Column')) {
          const value = headerName.replace('Column', '');
          if (value && Number(value.trim()) && Number(value.trim()) > acc) {
            return +value;
          }
        }
        return acc;
      }, 0),
    [columnDefs]
  );

  const getLastVisibleColumn = () => {
    const lastColumn = [];
    columnState.map(({ hide, colId }) => !hide && lastColumn.push(colId));
    return lastColumn[lastColumn.length - 1];
  };

  const insertNewColumn = async () => {
    if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    }
    dispatch(setIsInsertingNewColumn(true));

    try {
      const payload = {
        formula: '""',
        columnName: `Column ${lastNumberOfDefaultColumnName + 1}`,
        afterColumnId: getLastVisibleColumn(),
      };

      const endpoint = `${URL_DATASET}/${metadata?.FileUuid}/${FORMULA}`;
      const result = await post(endpoint, payload);

      if (!result.Success) {
        ShowToast({
          type: TOAST_TYPE_ERROR,
          text: TOAST_TEXT_APPLY_FUNCTION_ERROR,
          errorContext: result,
          endpoint: endpoint,
          payload: payload,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        });
      } else {
        await dispatch(fetchOpenedFile(metadata?.FileUuid));
        dispatch(
          addMixpanelEvent({
            eventName: 'Function Used',
            eventProps: { FunctionName: 'Formula' },
            userIncrementName: '# of function use',
          })
        );
      }
    } catch (e) {
      throw new Error(e);
    } finally {
      dispatch(setIsInsertingNewColumn(false));
    }
  };

  useEffect(() => {
    const getColumnInfo = async () => {
      const uuid = getFileUuidFromPath();

      try {
        const metadata = await getClientState(uuid);
        setColumnInfo(metadata?.ColumnInfo || {});
      } catch (error) {
        console.error('Error fetching client state:', error);
      }
    };

    getColumnInfo();
  }, [columnDefs]);

  const refreshAllCalculations = async () => {
    const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
    const toastErrorText =
      'An error occurred while refreshing the formulas. Please try again later or contact support.';

    try {
      const formulas = Object.keys(columnInfo).map((columnId) => {
        const { formula } = columnInfo[columnId];
        formula.columnId = columnId;
        return formula;
      });

      if (formulas.length === 0) {
        return;
      }

      for (const formula of formulas) {
        const formulaToEdit = formula;
        const columnName = columnDefs.find(
          (colDef) => colDef.colId === formula.columnId
        ).headerName;

        await dispatch(
          applyFormula({
            requestFormula: formulaToEdit.resolved,
            columnName,
            formulaToEdit,
            insertNewColumnNextTo: formulaToEdit.columnId,
            agGrid,
            currentModalOption: 'formula',
            fileAndFunction,
            toastErrorText,
          })
        );
      }

      window.location.reload(); // TODO: Added this line to refresh the page after applying the formulas. This is a temporary solution and should be replaced with a better one. The problem is that sometimes all the formula column cells becomes blank.
    } catch (error) {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: toastErrorText,
        errorContext: error,
        fileAndFunction,
      });
    }
  };

  const resetSheet = async () => {
    if (params.fileUuid) {
      navigate(`/${URL_SPREADSHEET}/${params?.name}/${params?.uuid}`);
    }
    try {
      dispatch(restoreGridState({ ...initialState.clientState, sidePanel }));

      const columnState = agGrid.api.getColumnState().map((column) => {
        return {
          ...column,
          hide: column.colId === ROW_NUMBER_COLUMN_ID,
          rowGroup: false,
          rowGroupIndex: null,
          aggFunc: null,
        };
      });

      const originalColumnState = columnState;
      dispatch(setColumnState(originalColumnState));

      if (originalColumnState.some((column) => column.aggFunc)) {
        const data = {
          ...agGrid.api.getGridOption('serverSideDatasource')?.state,
          ungroupedAggregations: originalColumnState
            .filter(
              (column) =>
                column.colId !== AG_GRID_AUTO_COLUMN &&
                column.colId !== ROW_NUMBER_COLUMN_ID &&
                column.id !== AG_GRID_AUTO_COLUMN
            )
            .map((column) => ({
              id: column.colId,
              field: column.colId,
              displayName: agGrid.api.getColumnDef(column.colId).headerName,
              aggFunc: column.aggFunc,
            })),
        };
        const fileId = getFileUuidFromPath();
        const response = await post(`${FILE}/${fileId}/${AGGREGATION}`, data);
        dispatch(setAggregationEndpointValues(response.results));
      }
      dispatch(setIsAggregationEndpointValuesLoading(false));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsAggregationEndpointValuesLoading(false));
    }

    dispatch(
      addMixpanelEvent({
        eventName: 'Reset Sheet (tool panel)',
        eventProps: {},
        userIncrementName: '# of clicks',
      })
    );
  };

  const openModal = (modalName, modalProps) => {
    flushSync(() =>
      dispatch(showModal({ name: modalName, props: { ...modalProps, getGridRequestData } }))
    );
  };

  const createAndOpenNewBlankSheet = async () => {
    const errorToastText = 'Error creating new blank sheet. Please try again or contact support.';
    const filename = 'Untitled Sheet';
    const endpoint = 'files/blank';
    const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
    let payload = {};

    const showErrorToast = (errorContext) => {
      ShowToast({
        type: TOAST_TYPE_ERROR,
        text: errorToastText,
        errorContext: errorContext,
        endpoint: endpoint,
        payload: payload,
        fileAndFunction: fileAndFunction,
      });
    };

    try {
      let folderHandle = metadata?.ParentDirectory;
      if (folderHandle) {
        const permissionsResponse = await get(`dataset/${folderHandle}/permissions`);
        if (!permissionsResponse?.permissions.includes(PERMISSION_WRITE)) {
          folderHandle = null;
        }
      }

      payload = {
        filename,
        folderHandle: folderHandle,
        numCols: 10,
        numRows: 10,
      };

      const result = await post(endpoint, payload);

      if (result.Success) {
        openSpreadsheet(result.Handle, filename);
      }

      if (!result.Success) {
        showErrorToast(result);
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const openSheetDetails = () => {
    dispatch(setGridSidePanel({ closed: false, chosenTab: SIDE_PANEL_INFO_PANEL }));
  };

  const isCopySelectedRangeToClipboardDisabled = !ranges?.length;

  const copySelectedRangeToClipboard = () => {
    const { columnCount, rowCount } = getSelectionRanges(agGrid.api.getCellRanges());
    agGrid.api.copySelectedRangeToClipboard();
    flushSync(() =>
      dispatch(
        addMixpanelEvent({
          eventName: 'Copy Data (client) (edit menu dropdown)',
          eventProps: { columnCount, rowCount },
        })
      )
    );
  };

  const pastFromClipboard = () =>
    flushSync(() =>
      dispatch(
        showModal({
          name: MODAL_PASTE_FROM_CONTEXT_MENU,
          props: {
            pasteFromContextMenu: true,
          },
        })
      )
    );

  const onMenuTitleClickAction = () =>
    !user?.is_anonymous && isFileShared && hasNoWritePermission(currentFile)
      ? openModal(MODAL_MAKE_COPY)
      : () => {};

  const onTextWrap = () => {
    const selectedColumns = agGrid?.api?.getCellRanges()[0]?.columns;

    if (selectedColumns?.length) {
      const updatedColumnDefs = columnDefs.map((column) => {
        if (selectedColumns.find(({ colId }) => colId === column.field)) {
          return { ...column, wrapText: !column?.wrapText, autoHeight: !column?.wrapText };
        }

        return column;
      });

      dispatch(
        addMixpanelEvent({
          eventName: 'Wrap Text',
          eventProps: {
            current_filename: metadata?.FileName,
          },
          userIncrementName: '# of function use',
        })
      );

      dispatch(setColumnDefs(updatedColumnDefs));
    }

    agGrid.api.refreshServerSide({ purge: true });
  };

  const addRowAbove = () => {
    addRow(0, agGrid, isPreviewMode, showPreviewModeUpsellModal);
  };

  const addRowBelow = () => {
    addRow(rowCount, agGrid, isPreviewMode, showPreviewModeUpsellModal);
  };

  const canViewSheetHistory = user.userProperties?.canUseAuditLog;
  const sheetHistory = () => {
    user?.is_anonymous
      ? dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }))
      : dispatch(
          showModal({
            name: canViewSheetHistory ? MODAL_AUDIT_LOG : MODAL_GENERAL_UPGRADE,
            props: { upgradeType: canViewSheetHistory ? '' : 'sheetHistory' },
          })
        );
  };

  /**
   * Returns an async event handler for exporting data using the specified connector.
   *
   * It constructs an export URL for the `/nexus` route that includes
   * query parameters for the connector type, file name, and file UUID.
   *
   * When the exportAsLiveView flag is true, the URL will include "&isLiveQuery=true".
   * This instructs the backend to create a database view instead of a one-off CSV file export.
   * exportAsLiveView is currently only supported by Snowflake connector.
   */
  const handleConnectorExport = (connector, exportAsLiveView) => {
    return async () => {
      if (user?.is_anonymous) {
        dispatch(showModal({ name: MODAL_REQUIRE_ACCOUNT }));
      } else if (isPreviewMode) {
        showPreviewModeUpsellModal('Connect to data warehouse or database on the Enterprise Plan');
      } else if (canAccessEnterpriseConnectors && connector === 'hubspot' && !hubspotAccess) {
        dispatch(
          showModal({
            name: MODAL_GENERAL_UPGRADE,
            props: { upgradeType: `${connector}ExportExisting` },
          })
        );
      } else if (!canAccessEnterpriseConnectors && (connector !== 'hubspot' || !hubspotAccess)) {
        dispatch(
          showModal({
            name: MODAL_GENERAL_UPGRADE,
            props: { upgradeType: `${connector}Export` },
          })
        );
      } else {
        const fileName = metadata?.FileName;
        const fileUuid = metadata?.FileUuid;
        openInNewTab(
          `/nexus?c=${connector}&type=export&fileName=${fileName}&fileUuid=${fileUuid}${
            exportAsLiveView ? '&isLiveQuery=true' : ''
          }`
        );
      }
    };
  };

  const menuItems = getMenuItems(
    openModal,
    firstRowAsHeader,
    headerAsFirstRow,
    openExportFileModal,
    openSharePopup,
    openMakeCopyFileModal,
    openRenameDropdown,
    openDeleteRowModal,
    openDeleteColumnsPopup,
    openDeleteDuplicatesModal,
    disableDelete,
    shouldDisableActions,
    canSplitSheet,
    user?.is_anonymous,
    limitedAnalystMenu,
    isFileShared,
    currentFile,
    insertNewColumn,
    createAndOpenNewBlankSheet,
    openSplitToMultipleCVSsPopup,
    openSheetDetails,
    copySelectedRangeToClipboard,
    pastFromClipboard,
    isCopySelectedRangeToClipboardDisabled,
    onTextWrap,
    rowCount,
    addRowAbove,
    addRowBelow,
    onMenuTitleClickAction,
    canViewSheetHistory,
    sheetHistory,
    handleConnectorExport,
    canAccessBusinessConnectors,
    canAccessEnterpriseConnectors,
    autoSizeAll,
    refreshAllCalculations,
    user,
    enableChatSupport,
    isSnowflakeFile,
    isLiveQuery,
    canShareLiveQuery,
    hubspotAccess,
    isPreviewMode
  );

  const onBtnWithoutMenuClick = (title) => {
    flushSync(() => dispatch(showModal({ name: title })));
  };

  const onChartBtnClick = () => {
    if (isPreviewMode) {
      return showPreviewModeUpsellModal();
    } else {
      chartData();
      dispatch(
        addMixpanelEvent({
          eventName: 'Chart data',
          eventProps: {},
          userIncrementName: '# of clicks on the "Chart data" button',
        })
      );
    }
  };

  const onEnrichmentBtnClick = () => {
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    } else if (isLiveQuery) {
      dispatch(showModal({ name: MODAL_LIVE_QUERY }));
    } else if (!user?.is_anonymous && isFileShared && hasNoWritePermission(currentFile)) {
      dispatch(showModal({ name: MODAL_MAKE_COPY }));
    } else {
      dispatch(showModal({ name: MODAL_DATA_ENRICHMENT }));
    }
  };

  const renderFilterButton = () => {
    const filterCount =
      getCountFiltersInModel(filterModel?._cnf_) +
      getCountFiltersInModel(filterModel?._aggregate_cnf_);

    return (
      <Button
        dataCy='toolpanel-filter'
        onClick={() =>
          isMobileScreen && isMobileExperienceActive
            ? dispatch(setIsMobileExperienceModalOpen(true))
            : onBtnWithoutMenuClick(FILTER)
        }
        color={
          filterModel?._cnf_?.length > 0 ||
          (filterModel?._aggregate_cnf_?.length > 0 && isGroupedColumns)
            ? 'sunrise-200'
            : 'shadow'
        }
        iconName='filter'
        variant={
          filterModel?._cnf_?.length > 0 ||
          (filterModel?._aggregate_cnf_?.length > 0 && isGroupedColumns)
            ? 'solid'
            : 'ghost'
        }
        className={clsx(
          '!h-7',
          (filterModel?._cnf_?.length > 0 ||
            (filterModel?._aggregate_cnf_?.length > 0 && isGroupedColumns)) &&
            'bg-sunrise-200 hover:bg-sunrise-300 active:bg-sunrise-400',
          'toolbar-button'
        )}
        disabled={!currentFile}
      >
        {filterModel?._cnf_?.length || filterModel?._aggregate_cnf_?.length ? (
          <span data-cy='filled-filters'>
            Filtered by <span data-cy='filled-filters'>{filterCount}</span>{' '}
            {filterCount > 1 ? 'fields' : 'field'}
          </span>
        ) : (
          <span data-cy='empty-filters'>{'Filter'}</span>
        )}
      </Button>
    );
  };

  const onHideColumnsButtonClick = () => {
    dispatch(setProperMobileDrawerContent(COLUMN_MANAGER));
  };

  const getEnrichmentCreditsValue = () => {
    try {
      const { Limit, Used } = enrichmentCredits;
      return parseRowCount(Limit - Used);
    } catch (e) {
      return 0;
    }
  };

  const enrichmentTooltipText = (
    <div>
      <div className='flex items-center justify-start mb-2 text-xs font-semibold lowercase text-sunrise-600'>
        <Icon name='coins' color='#F2CA00' className='mr-2' />
        <span className='uppercase'>{getEnrichmentCreditsValue()}</span>
        &nbsp; credits available
      </div>
      <div>
        <p>Add new information to your data from </p>
        <p>third-party sources with your credits.</p>
      </div>
    </div>
  );

  const getSheetViewName = () => {
    const targetView = sheetViews.find(({ id }) => id === params.fileUuid);
    if (targetView?.name) {
      return targetView.name;
    } else {
      return 'Views';
    }
  };

  useEffect(() => {
    dispatch(getAllViews());
  }, [dispatch]);

  return (
    <div className='toolPanelContainer !flex !flex-col md:w-full'>
      {isMobileScreen && isMobileExperienceActive ? (
        <div className='flex !items-center px-3 py-2'>
          <Button
            disabled={!currentFile}
            dataCy='toolpanel-chartData'
            onClick={onHideColumnsButtonClick}
            iconName='eye-slash'
            className='mr-1 toolbar-button'
            variant='ghost'
          >
            Manage Columns
          </Button>
          {renderFilterButton()}
        </div>
      ) : (
        <>
          <div className='flex items-center justify-between px-2 py-1 bg-white border-b md:justify-start border-ui-200 h-9'>
            <div className='flex justify-start md:w-1/3'>
              {Object.keys(menuItems).map((itemConfigKey, index) => {
                const itemConfig = menuItems[itemConfigKey];
                const isMenuItemsDisabled =
                  itemConfig.title !== 'Help' &&
                  (user?.is_anonymous || (isPreviewMode && !hasNoWritePermission(currentFile)));

                return (
                  <CustomMenu
                    menuConfig={itemConfig}
                    key={index}
                    isMenuItemsDisabled={isMenuItemsDisabled}
                    openModal={openModal}
                    setIsMenuOpen={setIsMenuOpen}
                    isLiveQuery={isLiveQuery}
                  />
                );
              })}
            </div>

            <div className='md:w-2/3'>
              {navigationProps.showNavigation && (
                <Navigation
                  isAppliedIOCRows={navigationProps.isAppliedIOCRows}
                  toggleIOCsPopup={navigationProps.toggleIOCsPopup}
                />
              )}
            </div>
          </div>
          <div className='flex items-center gap-1 px-2 h-9'>
            <div id='view-button'>
              <Popover
                isStatic={false}
                isButtonIcon={false}
                isCloseButton={false}
                buttonTextClassname={'!flex text-sm toolbar-button '}
                buttonClassname={clsx(
                  '!focus:outline-0 !focus-within:outline-none !focus-visible:outline-none !outline-0 !ring-0 !h-7 border rounded !px-2 !justify-start mt-[2px]'
                )}
                panelClassname={clsx(
                  '!transform-none !focus:outline-0 !focus-within:outline-none !focus-visible:outline-none !outline-0 !ring-0',
                  !sheetViews?.length ? '!w-[350px]' : '!w-auto !min-w-[350px] !w-96'
                )}
                content={<Views />}
                disabled={!currentFile}
              >
                <div
                  data-cy='sheet-views-button'
                  className='flex items-center justify-start whitespace-nowrap'
                >
                  <Icon
                    name='table'
                    className='mr-[6px] min-w-max'
                    color='#6B61DD'
                    weight='duotone'
                  />
                  <div className='text-left truncate mt-[2px] w-fit max-w-72'>
                    {getSheetViewName()}
                  </div>
                  <Icon name='caret-down' className='ml-3' size={16} />
                </div>
              </Popover>
            </div>

            <Button
              disabled={!currentFile || isResetButtonEnable}
              onClick={resetSheet}
              variant='ghost'
              iconName='arrow-counter-clockwise'
              className='!h-7'
              dataCy='reset-button'
            >
              Reset
            </Button>

            <hr className='mx-2 border-l h-7 border-ui' />

            <Popover
              isStatic={false}
              isButtonIcon={false}
              isCloseButton={false}
              buttonTextClassname={'!flex text-sm toolbar-button'}
              buttonClassname={clsx(
                '!focus:outline-0 !focus-within:outline-none !focus-visible:outline-none !outline-0 !ring-0 !h-7 mt-[2px]',
                numberOfHiddenColumns && '!text-ui !bg-capri-100 hover:!bg-capri-200'
              )}
              panelClassname='!w-auto !max-w-[800px] !min-w-[400px] !transform-none !focus:outline-0 !focus-within:outline-none !focus-visible:outline-none !outline-0 !ring-0'
              content={
                <CustomColumnManager
                  openDeleteColumnsPopup={openDeleteColumnsPopup}
                  agGrid={agGrid}
                />
              }
              disabled={!currentFile}
            >
              <div
                data-cy='toolpanel-manage-columns'
                className='flex items-center justify-start whitespace-nowrap'
              >
                <Icon name='eye' className='mr-2' size={18} />
                {numberOfHiddenColumns
                  ? `${numberOfHiddenColumns} hidden ${
                      numberOfHiddenColumns > 1 ? 'fields' : 'field'
                    }`
                  : 'Manage Columns'}
              </div>
            </Popover>

            {renderFilterButton()}

            <Button
              dataCy='toolpanel-groupBy'
              onClick={() => onBtnWithoutMenuClick(GROUP)}
              iconName='group'
              variant={groupedColumnsLength ? 'solid' : 'ghost'}
              color={groupedColumnsLength ? 'violetWeb' : 'shadow'}
              className={clsx(
                'toolbar-button !h-7',
                groupedColumnsLength &&
                  '!bg-ocean-blue-100 hover:!bg-ocean-blue-200 active:bg-ocean-blue-200'
              )}
              disabled={!currentFile}
            >
              {groupedColumnsLength
                ? `${isPivotEnabled ? 'Pivoted' : 'Grouped'} by ${groupedColumnsLength} ${
                    groupedColumnsLength > 1 ? 'fields' : 'field'
                  }`
                : 'Group'}
            </Button>

            <Button
              dataCy='toolpanel-sort-column'
              variant={sortModel.length ? 'solid' : 'ghost'}
              color={sortModel.length ? 'pear' : 'shadow'}
              onClick={() => onBtnWithoutMenuClick(SORT)}
              iconName='arrows-down-up'
              disabled={!currentFile}
              className={clsx('!h-7', sortModel.length && ' !bg-pear-200 hover:!bg-pear-300')}
            >
              {sortModel.length
                ? `Sorted by ${sortModel.length} ${sortModel.length > 1 ? 'fields' : 'field'}`
                : 'Sort'}
            </Button>
            {limitedAnalystMenu ? null : (
              <Tooltip side='bottom' text={enrichmentTooltipText} asChild>
                <Button
                  disabled={!currentFile}
                  dataCy='toolpanel-Enrichment'
                  onClick={onEnrichmentBtnClick}
                  iconName='atom'
                  className='toolbar-button !h-7'
                  variant='ghost'
                >
                  Enrich Data
                </Button>
              </Tooltip>
            )}

            <Tooltip
              side='bottom'
              text={!agGrid?.api?.getCellRanges()?.length && 'Select a range to chart it'}
              asChild
            >
              <Button
                disabled={!currentFile || !agGrid?.api?.getCellRanges()?.length}
                dataCy='toolpanel-chartData'
                onClick={onChartBtnClick}
                iconName='chart-bar'
                className='toolbar-button !h-7'
                variant='ghost'
              >
                Chart Data
              </Button>
            </Tooltip>
            <ViewOnly buttonVariant='outline' buttonSize='medium' otherClassNames='!h-7' />
          </div>
        </>
      )}
    </div>
  );
}
